import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  authenticationContext,
  commonActions,
  commonActionUtils,
  uiActions
} from '@codametrix/ui-common';
import history from '../history';
import {
  displayContexts,
  fetchContexts as contextApi,
  shouldRefresh
} from './contexts.action';
import { jumpURL } from './jump';
import { dismissModal } from './feedback.actions';
import { loading, logout, setActiveUser } from './ui-core.actions';
import { navigateContext } from './navigation.actions';
import { sendActivityHeartbeat } from '@codametrix/client-session';
import { dispatchSessionHeartbeatMiss } from '../core/session-heartbeat-miss';

const { UserInterface } = commonActions;
const { shimContext } = commonActionUtils;

const fetchContexts = createAsyncThunk<
  CMx.Context,
  CMx.NavData | null,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  UserInterface.BOOTSTRAP,
  async (navData, { dispatch, getState }): Promise<CMx.Context> => {
    const state = getState() as any;
    // Prefer context from state if available, as navData may have been captured
    // before the authentication channel resolved and updated the state.
    const currentContext = state.ui?.context;
    const optContext = currentContext && currentContext.activeContext !== null 
      ? currentContext 
      : navData?.context;

    // in some cases the activeContext will already have been
    // determined.  if this is the case, then simply return
    // the context.  if the user needs to select the context
    // later we should provision this in the UI.
    if (optContext && optContext.activeContext !== null) {
      authenticationContext.setContextId(optContext.activeContextId);
      dispatch(
        uiActions.saveServiceLineCapability(
          optContext?.activeContext?.serviceLineName || ''
        )
      );
      dispatch(uiActions.awaitingResources(false));
      return optContext;
    } else {
      const userContexts = await contextApi(dispatch as any);

      const shimmed = shimContext(userContexts);
      const { contexts, user } = userContexts;

      dispatch(setActiveUser(user));

      if (shimmed.activeContext === null && contexts.length > 1) {
        dispatch(displayContexts(contexts));
      } else {
        const jumped = await jumpURL(shimmed, dispatch as any);
        if (!jumped) {
          dispatch(uiActions.chooseContext(shimmed));
          dispatch(navigateContext({ context: shimmed }) as any);
          dispatch(loading(false));
        }
      }
      dispatch(
        uiActions.saveServiceLineCapability(
          shimmed?.activeContext?.serviceLineName || ''
        ) as any
      );
      return shimmed;
    }
  }
);

type submitArgs = {
  name: string;
  params?: { [key: string]: any };
};

const modalSubmit = createAsyncThunk<
  void,
  submitArgs,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  UserInterface.MODAL_SUBMIT,
  async (args, { dispatch }) => {
    const { name, params } = args;

    switch (name) {
      case UserInterface.LOGOUT:
        dispatch(logout());
        if (params?.isSSOUser && params?.orgCode?.length) {
          history.replace(`/sso/${params?.orgCode}`);
        } else {
          history.push(`/`);
        }
        break;
      case UserInterface.REFRESH:
        dispatch(shouldRefresh());
        dispatch(dismissModal());
        await sendActivityHeartbeat({
          contextId: authenticationContext.getContextId(),
          shouldSendHeartbeat: true,
          onSessionMiss: () => dispatchSessionHeartbeatMiss(dispatch),
        });
        break;
      default:
        break;
    }
  }
);

export { fetchContexts, modalSubmit };
